import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import DefaultLayout from "../layout/DefaultLayout";
import ErrorPage from "../pages/ErrorPage";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { AuthorizedRoute } from "../components/AuthorizedRoute";
import { RootRedirect } from "../components/RootRedirect";

// ─── Auth pages (small, load immediately) ────────────────────
import LoginPage from "../pages/LoginPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";

// ─── Lazy-loaded pages (code-split per route) ────────────────
const Home = lazy(() => import('../pages/Home'));
const Cotization = lazy(() => import('../pages/Cotizacion'));
const Reservation = lazy(() => import('../pages/Reservation'));
const Clients = lazy(() => import('../pages/Clients'));
const ClientCotizations = lazy(() => import('../pages/ClientCotizations'));
const Destination = lazy(() => import('../pages/Destination'));
const DestinationDetail = lazy(() => import('../pages/DestinationDetail'));
const Aerolines = lazy(() => import('../pages/Aeroline'));
const Hotels = lazy(() => import('../pages/Hotel'));
const ReservationDetail = lazy(() => import('../pages/ReservationDetail'));
const ReservationEdit = lazy(() => import('../pages/ReservationEdit'));
const Tours = lazy(() => import('../pages/Tour'));
const Operators = lazy(() => import('../pages/Operator'));
const CruiseShips = lazy(() => import('../pages/Cruises/CruiseShips'));
const CruiseCabins = lazy(() => import('../pages/Cruises/CruiseCabins'));
const ProposalPreview = lazy(() => import('../pages/ProposalPreview'));
const CruiseReservationWizard = lazy(() => import('../pages/Cruises/CruiseReservationWizard'));
const CruisesList = lazy(() => import('../pages/Cruises/CruisesList'));
const CruiseDetail = lazy(() => import('../pages/Cruises/CruiseDetail'));
const FlightsList = lazy(() => import('../pages/Flights/FlightsList'));
const FlightReservationWizard = lazy(() => import('../pages/Flights/FlightReservationWizard'));
const FlightDetail = lazy(() => import('../pages/Flights/FlightDetail'));
const ExcursionsList = lazy(() => import('../pages/Excursions/ExcursionsList'));
const ExcursionReservationWizard = lazy(() => import('../pages/Excursions/ExcursionReservationWizard'));
const ExcursionDetail = lazy(() => import('../pages/Excursions/ExcursionDetail'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const CommissionsPage = lazy(() => import('../pages/CommissionsPage'));
const UsersPage = lazy(() => import('../pages/Users'));
const RolesPage = lazy(() => import('../pages/Roles'));
const Analytics = lazy(() => import('../pages/Analytics'));
const SettingsPage = lazy(() => import('../pages/Settings'));
const AccountingDashboard = lazy(() => import('../pages/Accounting/AccountingDashboard'));
const ExpensesDashboard = lazy(() => import('../pages/Expenses/ExpensesDashboard'));
const TreasuryDashboard = lazy(() => import('../pages/Accounting/Treasury/TreasuryDashboard'));
const InvoiceCreate = lazy(() => import('../pages/Accounting/InvoiceCreate'));
const InvoiceEdit = lazy(() => import('../pages/Accounting/InvoiceEdit'));
const ExternalTools = lazy(() => import('../pages/ExternalTools'));
const Chats = lazy(() => import('../pages/Chats'));
const BookingSearch = lazy(() => import('../pages/PublicPortal/BookingSearch'));
const BookingDashboard = lazy(() => import('../pages/PublicPortal/BookingDashboard'));
const PromotionsList = lazy(() => import('../pages/Promotions/PromotionsList'));
const PromotionForm = lazy(() => import('../pages/Promotions/PromotionForm'));
const CallRoom = lazy(() => import('../pages/PublicPortal/CallRoom'));
const HotelReservationsList = lazy(() => import('../pages/Hotels/HotelReservationsList'));
const HotelReservationWizard = lazy(() => import('../pages/Hotels/HotelReservationWizard'));
const HotelReservationDetail = lazy(() => import('../pages/Hotels/HotelDetail'));
const CheckinDashboard = lazy(() => import('../pages/Checkin/CheckinDashboard'));
const ReturnsDashboard = lazy(() => import('../pages/Checkin/ReturnsDashboard'));
const TasksBoard = lazy(() => import('../pages/Tasks/TasksBoard'));
const MessageTemplates = lazy(() => import('../pages/MessageTemplates'));
const TransfersList = lazy(() => import('../pages/Transfers/TransfersList'));
const TransferReservationWizard = lazy(() => import('../pages/Transfers/TransferReservationWizard'));
const AuditLogPage = lazy(() => import('../pages/AuditLog/AuditLogPage'));

export const routes = [
    {
        path: '/call/:callId',
        element: (
            <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
                    <div className="w-10 h-10 border-4 border-[#EBF0FA] border-t-[#3245a4] rounded-full animate-spin"></div>
                </div>
            }>
                <CallRoom />
            </Suspense>
        )
    },
    {
        path: '/mi-viaje',
        element: (
            <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
                    <div className="w-10 h-10 border-4 border-[#EBF0FA] border-t-[#3245a4] rounded-full animate-spin"></div>
                </div>
            }>
                <BookingSearch />
            </Suspense>
        )
    },
    {
        path: '/mi-viaje/itinerario',
        element: (
            <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
                    <div className="w-10 h-10 border-4 border-[#EBF0FA] border-t-[#3245a4] rounded-full animate-spin"></div>
                </div>
            }>
                <BookingDashboard />
            </Suspense>
        )
    },
    {
        path: '/login',
        element: <LoginPage />
    },
    {
        path: '/reset-password',
        element: <ResetPasswordPage />
    },
    {
        element: (
            <ProtectedRoute>
                <DefaultLayout /></ProtectedRoute>
        ),
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <RootRedirect />,
            },
            {
                path: 'dashboard',
                element: (
                    <AuthorizedRoute moduleKey="dashboard">
                        <Home />
                    </AuthorizedRoute>
                ),
            },
            { path: 'cotizations', element: <AuthorizedRoute moduleKey="packages"><Cotization /></AuthorizedRoute> },
            { path: 'cruises', element: <AuthorizedRoute moduleKey="cruises"><CruisesList /></AuthorizedRoute> },
            { path: 'cruises/new', element: <AuthorizedRoute moduleKey="cruises"><CruiseReservationWizard /></AuthorizedRoute> },
            { path: 'cruises/:cruiseId', element: <AuthorizedRoute moduleKey="cruises"><CruiseDetail /></AuthorizedRoute> },
            { path: 'cruises/:cruiseId/edit', element: <AuthorizedRoute moduleKey="cruises"><CruiseReservationWizard /></AuthorizedRoute> },
            { path: 'promotions', element: <AuthorizedRoute moduleKey="promotions"><PromotionsList /></AuthorizedRoute> },
            { path: 'promotions/new', element: <AuthorizedRoute moduleKey="promotions"><PromotionForm /></AuthorizedRoute> },
            { path: 'promotions/:promotionId/edit', element: <AuthorizedRoute moduleKey="promotions"><PromotionForm /></AuthorizedRoute> },
            { path: 'flights', element: <AuthorizedRoute moduleKey="packages"><FlightsList /></AuthorizedRoute> },
            { path: 'flights/new', element: <AuthorizedRoute moduleKey="packages"><FlightReservationWizard /></AuthorizedRoute> },
            { path: 'flights/:flightId', element: <AuthorizedRoute moduleKey="packages"><FlightDetail /></AuthorizedRoute> },
            { path: 'flights/:flightId/edit', element: <AuthorizedRoute moduleKey="packages"><FlightReservationWizard /></AuthorizedRoute> },
            { path: 'hotel-reservations', element: <AuthorizedRoute moduleKey="hotel_reservations"><HotelReservationsList /></AuthorizedRoute> },
            { path: 'hotel-reservations/new', element: <AuthorizedRoute moduleKey="hotel_reservations"><HotelReservationWizard /></AuthorizedRoute> },
            { path: 'hotel-reservations/:hotelReservationId', element: <AuthorizedRoute moduleKey="hotel_reservations"><HotelReservationDetail /></AuthorizedRoute> },
            { path: 'hotel-reservations/:hotelReservationId/edit', element: <AuthorizedRoute moduleKey="hotel_reservations"><HotelReservationWizard /></AuthorizedRoute> },
            { path: 'excursions', element: <AuthorizedRoute moduleKey="excursions"><ExcursionsList /></AuthorizedRoute> },
            { path: 'excursions/new', element: <AuthorizedRoute moduleKey="excursions"><ExcursionReservationWizard /></AuthorizedRoute> },
            { path: 'excursions/:excursionId', element: <AuthorizedRoute moduleKey="excursions"><ExcursionDetail /></AuthorizedRoute> },
            { path: 'excursions/:excursionId/edit', element: <AuthorizedRoute moduleKey="excursions"><ExcursionReservationWizard /></AuthorizedRoute> },
            { path: 'transfers', element: <AuthorizedRoute moduleKey="transfers"><TransfersList /></AuthorizedRoute> },
            { path: 'transfers/new', element: <AuthorizedRoute moduleKey="transfers"><TransferReservationWizard /></AuthorizedRoute> },
            { path: 'transfers/:transferReservationId/edit', element: <AuthorizedRoute moduleKey="transfers"><TransferReservationWizard /></AuthorizedRoute> },
            { path: 'reservations', element: <AuthorizedRoute moduleKey="packages"><Reservation /></AuthorizedRoute> },
            { path: 'reservations/:reservationId/detail', element: <AuthorizedRoute moduleKey="packages"><ReservationDetail /></AuthorizedRoute> },
            { path: 'reservations/:reservationId/edit', element: <AuthorizedRoute moduleKey="packages"><ReservationEdit /></AuthorizedRoute> },
            { path: 'reservations/:reservationId/proposal', element: <AuthorizedRoute moduleKey="packages"><ProposalPreview /></AuthorizedRoute> },
            { path: 'destinations', element: <AuthorizedRoute moduleKey="destinations"><Destination /></AuthorizedRoute> },
            { path: 'destinations/:id', element: <AuthorizedRoute moduleKey="destinations"><DestinationDetail /></AuthorizedRoute> },
            { path: 'hotels', element: <AuthorizedRoute moduleKey="hotels"><Hotels /></AuthorizedRoute> },
            { path: 'tours', element: <AuthorizedRoute moduleKey="tours"><Tours /></AuthorizedRoute> },
            { path: 'transfers-catalog', element: <Navigate to="/destinations" replace /> },
            { path: 'operators', element: <AuthorizedRoute moduleKey="operators"><Operators /></AuthorizedRoute> },
            { path: 'aerolines', element: <AuthorizedRoute moduleKey="airlines"><Aerolines /></AuthorizedRoute> },
            { path: 'cruise-ships', element: <AuthorizedRoute moduleKey="cruise_ships"><CruiseShips /></AuthorizedRoute> },
            { path: 'cruise-cabins', element: <AuthorizedRoute moduleKey="cruise_cabins"><CruiseCabins /></AuthorizedRoute> },
            { path: 'customers', element: <AuthorizedRoute moduleKey="customers"><Clients /></AuthorizedRoute> },
            { path: 'customers/:id/reservations', element: <AuthorizedRoute moduleKey="customers"><ClientCotizations /></AuthorizedRoute> },
            { path: 'chats', element: <AuthorizedRoute moduleKey="chats"><Chats /></AuthorizedRoute> },
            { path: 'templates', element: <AuthorizedRoute moduleKey="chats"><MessageTemplates /></AuthorizedRoute> },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'commissions', element: <AuthorizedRoute moduleKey="commissions"><CommissionsPage /></AuthorizedRoute> },
            { path: 'users', element: <AuthorizedRoute moduleKey="users"><UsersPage /></AuthorizedRoute> },
            { path: 'roles', element: <AuthorizedRoute moduleKey="roles"><RolesPage /></AuthorizedRoute> },
            { path: 'analytics', element: <AuthorizedRoute moduleKey="analytics"><Analytics /></AuthorizedRoute> },
            { path: 'settings', element: <AuthorizedRoute moduleKey="settings"><SettingsPage /></AuthorizedRoute> },
            { path: 'treasury', element: <AuthorizedRoute moduleKey="treasury"><TreasuryDashboard /></AuthorizedRoute> },
            { path: 'accounting', element: <AuthorizedRoute moduleKey="accounting"><AccountingDashboard /></AuthorizedRoute> },
            { path: 'gastos-operativos', element: <AuthorizedRoute moduleKey="gastos_operativos"><ExpensesDashboard /></AuthorizedRoute> },
            { path: 'accounting/new', element: <AuthorizedRoute moduleKey="accounting"><InvoiceCreate /></AuthorizedRoute> },
            { path: 'accounting/:invoiceId/edit', element: <AuthorizedRoute moduleKey="accounting"><InvoiceEdit /></AuthorizedRoute> },
            { path: 'integrations/:toolId', element: <AuthorizedRoute moduleKey="settings"><ExternalTools /></AuthorizedRoute> },
            { path: 'seguimiento/checkin', element: <AuthorizedRoute moduleKey="checkin"><CheckinDashboard /></AuthorizedRoute> },
            { path: 'seguimiento/retornos', element: <AuthorizedRoute moduleKey="retornos"><ReturnsDashboard /></AuthorizedRoute> },
            { path: 'tasks', element: <AuthorizedRoute moduleKey="tasks"><TasksBoard /></AuthorizedRoute> },
            { path: 'audit-log', element: <AuthorizedRoute moduleKey="audit_log"><AuditLogPage /></AuthorizedRoute> },
        ]
    }, {
        path: '*',
        element: <h1>404 | Página no encontrada</h1>
    }
]